// @ts-nocheck
import React from 'react';
import { HelpCircle, Bell } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { openAppHelp, appHelpDescription } from '../../utils/appHelp';
import { useFirmLawFirmOptional } from '../firm/FirmLawFirmContext';
import { CLOSING_DATE_COLORS, closingDateVariant } from '../../utils/closingDate';
import { workStatusLabel } from '../firm/companyIntakeFields';
import { formatClosingDateBanner } from '../shared/CompanySummaryCard';
import UserMenu from './UserMenu';

/** Workspace meta strip for client shell (law firm · status · closing) — under compound title. */
export interface AppPageHeaderClientMeta {
  closingDate?: string | null;
  workStatus?: string | null;
  lawFirmName?: string | null;
}

export interface AppPageHeaderProps {
  /** When set, replaces the default title column (e.g. minimal “back” row). */
  leftContent?: React.ReactNode;
  /** Omit to show no page title in this bar (e.g. title lives on a content card). */
  title?: string;
  subtitle?: string;
  /** Narrow control shown before the title column (e.g. back chevron). */
  leading?: React.ReactNode;
  /** Shown after the page title on the same row (e.g. back action next to the name). */
  titleTrailing?: React.ReactNode;
  /**
   * Client shell: when set (and the user is a client), title becomes `Company Name / Page Title`,
   * mirroring the firm-shell `Firm Name / Page Title` pattern.
   */
  clientCompanyName?: string;
  /** Client shell: company in compound title; law firm + status (+ optional closing) on the line below. */
  clientMeta?: AppPageHeaderClientMeta;
  /**
   * `firmShell`: no bottom border — the firm layout draws a single line under logo + header so they align.
   */
  variant?: 'default' | 'firmShell';
}

/** Status text color for client meta strip (mirrors the old `CompanySummaryCard` workflow status pill). */
function clientWorkStatusColor(value: string): string {
  switch (value) {
    case 'in_progress':
      return 'text-blue-700';
    case 'review':
      return 'text-violet-700';
    case 'complete':
      return 'text-emerald-700';
    case 'on_hold':
      return 'text-amber-700';
    case 'intake':
    default:
      return 'text-slate-700';
  }
}

/**
 * Shell title — slightly larger than body for parity with workflow page headers.
 * (Keep font weight at `font-medium`.)
 */
/** Scales down on narrow phones so compound titles + controls fit without clipping. */
const titleClassName =
  'font-medium text-xl leading-tight text-emerald-800 truncate min-w-0 sm:text-2xl md:text-[1.65rem] md:leading-tight';

/** Compound `Context / Page` titles — one step smaller than standalone shell titles (less visual weight). */
const compoundTitleClassName =
  'font-medium text-lg leading-tight text-emerald-800 truncate min-w-0 sm:text-xl sm:leading-tight md:text-2xl';

const iconBtnClass =
  'w-9 h-9 rounded-full flex items-center justify-center bg-white text-gray-500 hover:bg-emerald-50 hover:text-[var(--green)] transition-colors';

/**
 * Shared top bar for admin, firm, and company shells — ParachuteIQ chrome (matches company layout).
 */
export default function AppPageHeader({
  leftContent,
  title,
  subtitle,
  leading,
  titleTrailing,
  clientCompanyName,
  clientMeta,
  variant = 'default',
}: AppPageHeaderProps) {
  const { user, logout } = useAuth();
  const firmLawFirm = useFirmLawFirmOptional();
  const firmLoading = user?.loginType === 'Firm' && firmLawFirm?.loading === true;
  const firmLoadError = user?.loginType === 'Firm' && firmLawFirm && !firmLawFirm.loading ? firmLawFirm.error : null;
  const firmNameRaw = firmLawFirm?.lawFirm?.firmName;
  const firmName = typeof firmNameRaw === 'string' ? firmNameRaw.trim() : '';
  const firmAddress = typeof firmLawFirm?.lawFirm?.address === 'string' ? firmLawFirm.lawFirm.address.trim() : '';
  const showFirmAddress = user?.loginType === 'Firm' && firmAddress.length > 0;
  const showTitle = typeof title === 'string' && title.trim() !== '';
  const showSubtitle = typeof subtitle === 'string' && subtitle.trim() !== '';
  const showFirmLoadError =
    user?.loginType === 'Firm' &&
    !firmLoading &&
    typeof firmLoadError === 'string' &&
    firmLoadError.trim() !== '' &&
    !firmName;
  /** Secondary line under shell title (firm address, page subtitle): tight to title row */
  const subtitleClassName = 'text-sm text-gray-500 mt-0';

  const metaLines =
    showFirmAddress || showSubtitle || showFirmLoadError ? (
      <div className="min-w-0">
        {showFirmAddress ? (
          <p className={`${subtitleClassName} leading-snug line-clamp-2`} title={firmAddress}>
            {firmAddress}
          </p>
        ) : null}
        {showSubtitle ? (
          <p className={showFirmAddress ? 'text-sm text-gray-500 truncate mt-0.5' : `${subtitleClassName} truncate`}>
            {subtitle}
          </p>
        ) : null}
        {showFirmLoadError ? (
          <p className="text-sm text-red-600 mt-1 line-clamp-2" title={firmLoadError}>
            {firmLoadError}
          </p>
        ) : null}
      </div>
    ) : null;

  const trimmedClientCompany = typeof clientCompanyName === 'string' ? clientCompanyName.trim() : '';
  const showClientCompound = user?.loginType === 'Client' && trimmedClientCompany.length > 0;

  const clientMetaParts: { key: string; node: React.ReactNode }[] = (() => {
    if (user?.loginType !== 'Client' || !clientMeta) return [];
    const list: { key: string; node: React.ReactNode }[] = [];
    const lf = (clientMeta.lawFirmName ?? '').trim();
    if (lf) {
      list.push({
        key: 'lawfirm',
        node: (
          <span className="inline-flex items-baseline gap-1 min-w-0">
            <span className="text-gray-400">Law firm</span>
            <span className="text-gray-700 truncate" title={lf}>
              {lf}
            </span>
          </span>
        ),
      });
    }
    const wsRaw = (clientMeta.workStatus ?? '').trim();
    if (wsRaw) {
      list.push({
        key: 'status',
        node: (
          <span className="inline-flex items-baseline gap-1">
            <span className="text-gray-400">Status:</span>
            <span className={clientWorkStatusColor(wsRaw)}>{workStatusLabel(wsRaw)}</span>
          </span>
        ),
      });
    }
    const closingRaw = (clientMeta.closingDate ?? '').trim();
    if (closingRaw) {
      const colorClass = CLOSING_DATE_COLORS[closingDateVariant(closingRaw)];
      list.push({
        key: 'closing',
        node: (
          <span className="inline-flex items-baseline gap-1">
            <span className="text-gray-400">Closing</span>
            <span className={`tabular-nums ${colorClass}`}>{formatClosingDateBanner(closingRaw)}</span>
          </span>
        ),
      });
    }
    return list;
  })();

  const clientMetaLine =
    clientMetaParts.length > 0 ? (
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-gray-500">
        {clientMetaParts.map((p, i) => (
          <React.Fragment key={p.key}>
            {i > 0 ? (
              <span aria-hidden className="text-gray-300">
                ·
              </span>
            ) : null}
            {p.node}
          </React.Fragment>
        ))}
      </div>
    ) : null;

  /** When API name is missing, show a label so the shell bar is never blank (matches old default `title=""` h1). */
  const firmFallbackTitle = user?.loginType === 'Firm' && !firmName && !firmLoading ? 'Law firm' : null;

  const shellClass =
    variant === 'firmShell'
      ? 'app-shell-header--row !border-b-0 h-full min-h-[var(--app-shell-header-height)]'
      : 'app-shell-header--row';
  return (
    <header className={shellClass}>
      {leftContent != null ? (
        <div className="min-w-0 flex-1 flex items-center">{leftContent}</div>
      ) : (
        <div className={`min-w-0 flex-1 flex ${leading != null ? 'items-center gap-3' : 'items-center gap-4'}`}>
          {leading != null ? <div className="shrink-0">{leading}</div> : null}
          <div className="min-w-0 flex-1">
            {user?.loginType === 'Firm' && firmName && showTitle ? (
              <>
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className={compoundTitleClassName}>{firmName}</span>
                    <span className="select-none text-lg font-light text-gray-300 sm:text-xl md:text-2xl" aria-hidden>
                      /
                    </span>
                    <h1 className={compoundTitleClassName}>{title}</h1>
                  </div>
                  {titleTrailing != null ? <div className="shrink-0">{titleTrailing}</div> : null}
                </div>
                {metaLines}
              </>
            ) : user?.loginType === 'Firm' && firmName && !showTitle ? (
              <>
                <h1 className={titleClassName}>{firmName}</h1>
                {metaLines}
              </>
            ) : user?.loginType === 'Firm' && !firmName && firmLoading && !showTitle ? (
              <>
                <h1 className={`${titleClassName} text-gray-400 animate-pulse`}>Loading law firm…</h1>
              </>
            ) : user?.loginType === 'Firm' && !firmName && !showTitle && firmFallbackTitle ? (
              <>
                <h1 className={titleClassName}>{firmFallbackTitle}</h1>
                {metaLines}
              </>
            ) : showClientCompound && showTitle ? (
              <>
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className={compoundTitleClassName}>{trimmedClientCompany}</span>
                    <span className="select-none text-lg font-light text-gray-300 sm:text-xl md:text-2xl" aria-hidden>
                      /
                    </span>
                    <h1 className={compoundTitleClassName}>{title}</h1>
                  </div>
                  {titleTrailing != null ? <div className="shrink-0">{titleTrailing}</div> : null}
                </div>
                {clientMetaLine}
                {metaLines}
              </>
            ) : showClientCompound && !showTitle ? (
              <>
                <h1 className={compoundTitleClassName}>{trimmedClientCompany}</h1>
                {clientMetaLine}
                {metaLines}
              </>
            ) : showTitle ? (
              <>
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                  <h1 className={`${titleClassName} min-w-0`}>{title}</h1>
                  {titleTrailing != null ? <div className="shrink-0">{titleTrailing}</div> : null}
                </div>
                {metaLines}
              </>
            ) : null}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <button
          type="button"
          className={iconBtnClass}
          title={appHelpDescription()}
          aria-label="Help — contact support"
          onClick={openAppHelp}
        >
          <HelpCircle size={20} aria-hidden />
        </button>
        <button
          type="button"
          disabled
          className={`${iconBtnClass} disabled:pointer-events-none disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-500`}
          title="Alerts (coming soon)"
          aria-label="Alerts — coming soon"
        >
          <Bell size={20} aria-hidden />
        </button>
        {user != null ? <UserMenu user={user} onLogout={logout} /> : null}
      </div>
    </header>
  );
}
